import React, { Fragment, useContext, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { 
  LogIn, 
  Shield, 
  Users, 
  X, 
  ChevronRight, 
  LogOut,
  Bell,
  ArrowLeft,
  Check
} from "lucide-react";
import useModal from "../../hooks/use-modal";
import API from "../../utils/api";
import { MainStore } from "../../stores/Main";

const ModalSelectAgency = ({ modalProps }) => {
  const { handleLogout } = useContext(MainStore);
  const { closeModal } = useModal();
  const [loginState, setLoginState] = useState("");
  const [alerts, setAlerts] = useState({});
  const [selectedAgency, setSelectedAgency] = useState(null);
  
  const agencies = [];
  const agIds = [];
  modalProps.accounts.forEach((account) => {
    const newAgency = {
      name: account.AgencyName,
      id: account.AgencyId,
      domain: account.AgencyDomain,
    };
    if (!agIds.includes(newAgency.id)) {
      agIds.push(newAgency.id);
      agencies.push(newAgency);
    }
  });

  const _selectAgency = (agency) => {
    setSelectedAgency(agency);
  };

  const doLogin = (id, domain, bypass) => {
    closeModal();
    modalProps.doLogin(id, domain, bypass);
  };

  const agencyLogin = (id) => {
    closeModal();
    modalProps.agencyRedirect(id);
  };

  const getAlerts = async () => {
    try {
      const response = await API.get("/alerts");
      const newAlerts = {};
      response.data.alert.forEach((alert) => {
        newAlerts[alert.agency_id] = true;
        if (alert.agency_id !== undefined) newAlerts.agencyAlert = true;
      });
      setAlerts(newAlerts);
    } catch (e) {}
  };

  useEffect(() => {
    getAlerts().then();
  }, []);

  // Animation variants
  const contentAnimation = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        staggerChildren: 0.1,
      },
    },
  };

  const itemAnimation = {
    hidden: { opacity: 0, x: -10 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.2 },
    },
  };

  return (
    <div className="flex flex-col h-full max-h-[100vh] sm:max-h-[90vh] p-0">
      {/* Modal Header */}
      <div className="relative bg-gradient-to-r from-blue-600 to-purple-600 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 flex-shrink-0 sm:rounded-t-2xl">
        <button
          onClick={closeModal}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 p-1.5 sm:p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors group z-10"
        >
          <X className="w-4 h-4 sm:w-5 sm:h-5 text-white group-hover:rotate-90 transition-transform duration-300" />
        </button>

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex items-center gap-3 sm:gap-4 pr-10"
        >
          <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center flex-shrink-0">
            <LogIn className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white truncate">
              Login
            </h2>
            <p className="text-blue-100 text-sm sm:text-base">
              {loginState === "" 
                ? "Please select where you wish to login to"
                : "Select an agency partner"}
            </p>
          </div>
        </motion.div>
      </div>

      {/* Modal Body */}
      <motion.div
        className="p-4 sm:p-6 lg:p-8 overflow-y-auto flex-1 min-h-0 overscroll-contain bg-gray-900"
        variants={contentAnimation}
        initial="hidden"
        animate="visible"
      >
        {loginState === "" ? (
          <>
            {/* Main Login Options */}
            <motion.div variants={itemAnimation} className="space-y-4">
              {/* Freede Dashboard Option */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => doLogin(null, null, true)}
                className="w-full p-4 bg-gray-800/50 hover:bg-gray-800/70 backdrop-blur rounded-xl border border-gray-700 hover:border-blue-500/50 transition-all group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                      <Shield className="w-6 h-6 text-blue-400" />
                    </div>
                    <div className="text-left">
                      <p className="text-white font-semibold text-base">
                        Freede dashboard and services
                      </p>
                      <p className="text-gray-400 text-sm mt-1">
                        Access your main Freede account
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {alerts[undefined] && (
                      <div className="relative">
                        <Bell className="w-5 h-5 text-yellow-400" />
                        <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                      </div>
                    )}
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" />
                  </div>
                </div>
              </motion.button>

              {/* Agency Partners Option */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setLoginState("agency")}
                className="w-full p-4 bg-gray-800/50 hover:bg-gray-800/70 backdrop-blur rounded-xl border border-gray-700 hover:border-purple-500/50 transition-all group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                      <Users className="w-6 h-6 text-purple-400" />
                    </div>
                    <div className="text-left">
                      <p className="text-white font-semibold text-base">
                        Agency partners linked to your profile
                      </p>
                      <p className="text-gray-400 text-sm mt-1">
                        Login to your agency accounts
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {alerts.agencyAlert && (
                      <div className="relative">
                        <Bell className="w-5 h-5 text-yellow-400" />
                        <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                      </div>
                    )}
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" />
                  </div>
                </div>
              </motion.button>
            </motion.div>

            {/* Logout Button */}
            <motion.div
              variants={itemAnimation}
              className="mt-8 pt-6 border-t border-gray-800"
            >
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleLogout(false)}
                className="w-full py-3 px-6 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 hover:border-red-500/50 text-red-400 hover:text-red-300 font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
              >
                <LogOut className="w-5 h-5" />
                Logout
              </motion.button>
            </motion.div>
          </>
        ) : (
          <>
            {/* Agency Selection State */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              {/* Agency List Header */}
              <div className="bg-gray-800/50 backdrop-blur rounded-xl border border-gray-700 p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">
                        Agency partners linked to your profile
                      </h3>
                      <p className="text-gray-400 text-sm">
                        Select an agency to continue
                      </p>
                    </div>
                  </div>
                  {alerts.agencyAlert && (
                    <div className="relative">
                      <Bell className="w-5 h-5 text-yellow-400" />
                      <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    </div>
                  )}
                </div>

                {/* Agency Radio Options */}
                <div className="space-y-3 mt-6">
                  {agencies.map((agency, index) => (
                    <motion.label
                      key={agency.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="flex items-center gap-3 p-3 bg-gray-700/30 hover:bg-gray-700/50 rounded-lg cursor-pointer transition-all group"
                    >
                      <input
                        type="radio"
                        name="agencychoice"
                        value={agency.id}
                        onChange={(e) => _selectAgency(e.target.value)}
                        className="w-5 h-5 text-purple-600 bg-gray-700 border-gray-600 focus:ring-0 focus:ring-offset-0 focus:outline-none cursor-pointer"
                      />
                      <span className="text-white group-hover:text-blue-300 transition-colors flex-1">
                        {agency.name}
                      </span>
                      {alerts[agency.id] && (
                        <div className="relative">
                          <Bell className="w-4 h-4 text-yellow-400" />
                          <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                        </div>
                      )}
                      {selectedAgency === String(agency.id) && (
                        <Check className="w-5 h-5 text-green-400" />
                      )}
                    </motion.label>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => agencyLogin(selectedAgency)}
                  disabled={selectedAgency === null}
                  className="w-full py-3 px-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 disabled:from-gray-600 disabled:to-gray-700 text-white font-semibold rounded-xl shadow-lg disabled:shadow-none transition-all"
                >
                  Continue Login
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setLoginState("");
                    setSelectedAgency(null);
                  }}
                  className="w-full py-3 px-6 bg-gray-800/50 hover:bg-gray-800/70 border border-gray-700 hover:border-gray-600 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Back
                </motion.button>
              </div>
            </motion.div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default ModalSelectAgency;